import { Button, Layout, Flex, message, Form, Input, Card } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setTokens } from "../../features/auth/authSlice";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  useLazyGetSiteInfoQuery,
  useUpdateEmailTemplateFolderMutation,
  useLazyGetSingleEmailTemplateFolderQuery,
} from "../../features/mailer/mailerApiSlice";
import LoadingComponentAntD from "../../components/loadingCompAntd/LoadingComponentAntD";
import { setSiteInfor } from "../../store/slice/siteInfoSlice";

const { Content } = Layout;

function EditFolder() {
  const [getSiteInfo] = useLazyGetSiteInfoQuery();
  const [
    getSingleEmailTemplateFolder,
    {
      data: folderData,
      isLoading: isFetchingFolder,
      isError: isFetchFolderError,
      error: fetchFolderError,
    },
  ] = useLazyGetSingleEmailTemplateFolderQuery();
  const dispatch = useDispatch();
  const [headersReady, setHeadersReady] = useState(false);
  const { user_token, auth, viewType, folderId } = useParams();
  const navigate = useNavigate();
  const storeAuth = useSelector((state) => state.auth);
  const [messageApi, contextHolder] = message.useMessage();
  const storeBaseUrl = useSelector((state) => state.baseUrl);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveEmailTemplateFolder] = useUpdateEmailTemplateFolderMutation();
  const [loading, setLoading] = useState(true);
  const [loaderContent, setLoaderContent] = useState("");

  const successMsg = (message) => {
    messageApi.open({
      type: "success",
      content: message,
    });
  };

  const errorMsg = (message) => {
    messageApi.open({
      type: "error",
      content: message,
    });
  };

  useEffect(() => {
    if (storeAuth?.authBearer && storeAuth?.token) {
      setHeadersReady(true);
    }
  }, [storeAuth, storeBaseUrl.value]);

  useEffect(() => {
    if (user_token && auth && viewType) {
      dispatch(
        setTokens({
          user: null,
          token: user_token,
          auth,
          viewType,
        })
      );
    }
  }, [user_token, auth, viewType]);

  useEffect(() => {
    const fetchSiteInfo = async () => {
      try {
        const { data, error } = await getSiteInfo();
        if (data) {
          dispatch(setSiteInfor(data));
        }
        if (error) {
          console.log("site-data::", error);
        }
      } catch (error) {
        console.error("Error fetching site info:", error);
      }
    };

    if (!headersReady) {
      fetchSiteInfo();
    }
  }, [getSiteInfo, storeAuth, headersReady]);

  useEffect(() => {
    const fetchFolderData = async () => {
      if (folderId) {
        setLoaderContent("Fetching Folder Data...");
        setLoading(true);
        await getSingleEmailTemplateFolder(folderId);
        setLoading(false);
        setLoaderContent("");
      }
    };
    fetchFolderData();
  }, [getSingleEmailTemplateFolder, folderId]);

  useEffect(() => {
    if (folderData && form) {
      form.setFieldsValue({
        folderName: folderData?.folder?.name,
      });
    } else if (isFetchFolderError) {
      console.error("Error fetching folder data:", fetchFolderError);
      errorMsg(
        fetchFolderError?.data?.message || "Failed to fetch folder data."
      );
    }
  }, [folderData, form, isFetchFolderError, fetchFolderError, errorMsg]);

  const navigateFolder = () => {
    navigate(`/${auth}/${user_token}/${viewType}`);
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setLoading(true);
    setLoaderContent("Saving...");

    try {
      const body = {
        name: values?.folderName,
      };

      const { data, error } = await saveEmailTemplateFolder({
        id: folderId,
        body,
      });

      if (data) {
        if (data.status) {
          successMsg(data.message);
          navigateFolder();
        } else {
          errorMsg(data.message);
        }
      }
      if (error) {
        errorMsg(error?.data?.message || "An error occurred while saving");
      }
    } catch (err) {
      errorMsg(err?.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsSubmitting(false);
        setLoaderContent("");
      }, 1000);
    }
  };

  return (
    <>
      {loading || isFetchingFolder ? (
        <LoadingComponentAntD content={loaderContent || "Loading..."} />
      ) : null}
      {contextHolder}
      <Content style={{ padding: "0 48px" }}>
        <div className="container">
          <div className="table-enter-leave-demo-action-bar">
            <Flex gap="middle" vertical>
              <Flex wrap gap="middle" justify="start">
                <Button
                  type="primary"
                  onClick={navigateFolder}
                  disabled={isSubmitting}
                >
                  <ArrowLeftOutlined />
                  Back to Folders
                </Button>
              </Flex>
            </Flex>
          </div>
          <div className="create-folder-container">
            <Card
              title="Rename Folder"
              className="create-folder-card"
              loading={isFetchingFolder}
            >
              <Form
                form={form}
                name="createFolder"
                layout="vertical"
                onFinish={onSubmit}
                autoComplete="off"
                className="create-folder-form"
              >
                <Form.Item
                  label="Folder Name"
                  name="folderName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter folder name!",
                    },
                    {
                      min: 3,
                      message:
                        "Folder name must be at least 3 characters long!",
                    },
                    {
                      max: 50,
                      message: "Folder name cannot exceed 50 characters!",
                    },
                    {
                      pattern: /^[a-zA-Z0-9\s\-_]+$/,
                      message:
                        "Folder name can only contain letters, numbers, spaces, hyphens, and underscores!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter folder name"
                    size="large"
                    disabled={isSubmitting}
                  />
                </Form.Item>

                <Form.Item>
                  <div className="create-folder-buttons">
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={isSubmitting}
                    >
                      Update Folder
                    </Button>
                    <Button
                      size="large"
                      onClick={() => form.resetFields()}
                      disabled={isSubmitting}
                    >
                      Reset
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </Content>
    </>
  );
}

export default EditFolder;
